"use client";

import { useCallback, useState } from "react";
import clsx from "clsx";
import {
  FacebookShareButton,
  FacebookIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
  XShareButton,
  XIcon,
} from "react-share";

type ShareButtonsProps = {
  url: string;
  title?: string;
  size?: number;
  variant?: "light" | "dark";
  utmContent?: string;
};

const UTM_SOURCES = {
  facebook: "facebook",
  whatsapp: "whatsapp",
  linkedin: "linkedin",
  x: "x",
  copy: "copy_link",
} as const;

function buildShareUrl(baseUrl: string, source: string, content?: string) {
  const url = new URL(baseUrl);
  url.searchParams.set("utm_source", source);
  url.searchParams.set("utm_medium", "social");
  url.searchParams.set("utm_campaign", "share");
  if (content) {
    url.searchParams.set("utm_content", content);
  }
  return url.toString();
}

function CopyLinkIcon({ size, fill }: Readonly<{ size: number; fill: string }>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={fill}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function CheckIcon({ size, fill }: Readonly<{ size: number; fill: string }>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={fill}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function ShareButtons({
  url,
  title,
  size = 32,
  variant = "dark",
  utmContent,
}: Readonly<ShareButtonsProps>) {
  const [copied, setCopied] = useState(false);

  const facebookUrl = buildShareUrl(url, UTM_SOURCES.facebook, utmContent);
  const whatsappUrl = buildShareUrl(url, UTM_SOURCES.whatsapp, utmContent);
  const linkedinUrl = buildShareUrl(url, UTM_SOURCES.linkedin, utmContent);
  const xUrl = buildShareUrl(url, UTM_SOURCES.x, utmContent);
  const copyUrl = buildShareUrl(url, UTM_SOURCES.copy, utmContent);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API unavailable */
    }
  }, [copyUrl]);

  const isLight = variant === "light";
  const iconFill = isLight ? "#ffffff" : undefined;
  const bgStyle = isLight ? { fill: "transparent" } : undefined;

  const shareIconProps = {
    size,
    round: true,
    ...(iconFill && { iconFillColor: iconFill }),
    ...(bgStyle && { bgStyle }),
  };

  return (
    <div className={clsx("flex items-center gap-2")}>
      <FacebookShareButton url={facebookUrl}>
        <FacebookIcon {...shareIconProps} />
      </FacebookShareButton>

      <WhatsappShareButton url={whatsappUrl} title={title}>
        <WhatsappIcon {...shareIconProps} />
      </WhatsappShareButton>

      <LinkedinShareButton url={linkedinUrl} title={title}>
        <LinkedinIcon {...shareIconProps} />
      </LinkedinShareButton>

      <XShareButton url={xUrl} title={title}>
        <XIcon {...shareIconProps} />
      </XShareButton>

      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Link copied" : "Copy link"}
        className={clsx(
          "inline-flex items-center justify-center rounded-full transition-colors cursor-pointer",
          isLight
            ? "text-white hover:bg-white/10"
            : "text-gray-600 hover:bg-gray-100",
        )}
        style={{ width: size, height: size }}
      >
        {copied ? (
          <CheckIcon
            size={size * 0.5}
            fill={isLight ? "#ffffff" : "#22c55e"}
          />
        ) : (
          <CopyLinkIcon
            size={size * 0.5}
            fill={isLight ? "#ffffff" : "#6b7280"}
          />
        )}
      </button>
    </div>
  );
}
